import { z } from 'zod';

import { baseEntitySchema } from './baseContract';
import { organizationSchema } from './organizationContract';
import {
  type SubscriptionTier,
  BillingInterval,
  billingIntervalEnum,
  subscriptionStatusEnum,
  subscriptionTierEnum,
  type Currency,
  Currency as CurrencyEnum,
  SubscriptionTier as SubscriptionTierEnum,
  BillingInterval as BillingIntervalEnum,
} from '../enums';

// Helper to preprocess dates that might be strings
// FIXME WTF is this? nikde s enepoužívá parse, tohle je k ničemu
const preprocessDate = (baseSchema: z.ZodDate | z.ZodNullable<z.ZodDate>) =>
  z.preprocess(
    arg => {
      if (typeof arg === 'string') {
        // Attempt to parse strings into Date objects and return directly
        return new Date(arg);
      }

      // Pass through other types (like actual Date objects or null/undefined)
      return arg;
    },
    // Apply base schema THEN refine to check for valid Date objects
    baseSchema.refine(
      val => {
        // If schema allows null and value is null, it's valid
        if (val === null && baseSchema instanceof z.ZodNullable) {
          return true;
        }

        // Otherwise, ensure it's a valid Date instance
        return val instanceof Date && !Number.isNaN(val.getTime());
      },
      {
        message: 'Invalid date string or format provided',
      },
    ),
  );

export const subscriptionSchema = baseEntitySchema.extend({
  tier: subscriptionTierEnum.optional(),
  status: subscriptionStatusEnum,
  maxDocuments: z.number().min(0).optional(),
  maxUsers: z.number().min(0).optional(),
  price: z.number().min(0).optional(),
  autoRenewal: z.boolean().default(false),
  autoRenewalLimit: z.number().nullable(),
  autoRenewalAmount: z.number().nullable(),
  startDate: preprocessDate(z.date()),
  endDate: preprocessDate(z.date().nullable()),
  billingInterval: billingIntervalEnum.default(BillingInterval.Month),
  stripeSubscriptionId: z.string().nullable(),
  stripePriceId: z.string().nullable(),
  stripeProductId: z.string().nullable(),
  metadata: z.record(z.string(), z.string().optional()).nullable(),
  autoRenewalChargedThisPeriod: z.number().min(0).default(0),
  autoRenewalPeriodStart: preprocessDate(z.date().nullable()),
  autoRenewalThreshold: z
    .number()
    .positive({ message: 'Threshold must be positive' })
    .min(5, { message: 'Threshold must be at least $5.00' })
    .nullable()
    .optional(),
  syncedAt: preprocessDate(z.date().nullable())
    .refine(date => !date || date <= new Date(), {
      message: 'Sync timestamp cannot be in the future',
    })
    .optional(),
  cancelsAtPeriodEnd: z.boolean().nullable().optional(),
});

export type SubscriptionDTO = z.infer<typeof subscriptionSchema>;

export const createSubscriptionSchema = subscriptionSchema
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .required({
    tier: true,
    status: true,
  })
  .strict();

export const updateSubscriptionSchema = subscriptionSchema
  .partial()
  .required({
    id: true,
  })
  .strict();

export type CreateSubscriptionDTO = z.infer<typeof createSubscriptionSchema>;
export type UpdateSubscriptionDTO = z.infer<typeof updateSubscriptionSchema>;

/**
 * Represents detailed information about a subscription tier,
 * combining static config and live pricing/metadata.
 */
export interface TierDetails {
  id: SubscriptionTier;
  name: string;
  description: string;
  features: string[];
  maxDocuments: number | string;
  maxUsers: number | string;
  prices: Array<{
    id: string;
    amount: number;
    interval: BillingInterval | null;
    currency: Currency;
  }>;
  metadata: Record<string, string>;
}

export const createCheckoutInputSchema = z.object({
  organizationId: z.string().uuid(),
  tier: subscriptionTierEnum,
  billingInterval: billingIntervalEnum.default(BillingInterval.Month),
  successUrl: z.string().url(),
  cancelUrl: z.string().url(),
});

export const updateSubscriptionInputSchema = z.object({
  organizationId: z.string().uuid(),
  tier: subscriptionTierEnum.optional(),
  billingInterval: billingIntervalEnum.optional(),
  autoRenewal: z.boolean().optional(),
  maxUsers: z.number().min(1).optional(),
  maxDocuments: z.number().positive().optional(),
  autoRenewalAmount: z
    .number()
    .positive()
    .min(5, { message: 'Auto-renewal amount must be at least $5' })
    .nullable()
    .optional()
    .transform(val => val ?? undefined),
  autoRenewalLimit: z
    .number()
    .nonnegative()
    .nullable()
    .optional()
    .transform(val => val ?? undefined),
  autoRenewalThreshold: z
    .number()
    .positive({ message: 'Threshold must be positive' })
    .min(5, { message: 'Threshold must be at least $5.00' })
    .nullable()
    .optional()
    .transform(val => val ?? undefined),
});

export const getActiveInputSchema = z.object({
  organizationId: z.string().uuid(),
});

export const getActiveOutputSchema = subscriptionSchema.extend({
  organization: organizationSchema,
});

export const cancelSubscriptionInputSchema = z.object({
  organizationId: z.string().uuid(),
});

export const createBillingPortalInputSchema = z.object({
  organizationId: z.string().uuid(),
  returnUrl: z.string().url().optional(),
});

export const topUpBalanceInputSchema = z.object({
  organizationId: z.string().uuid(),
  amount: z.number().min(5),
  successUrl: z.string().url(),
  cancelUrl: z.string().url(),
});

export const assignCustomPlanInputSchema = z.object({
  organizationId: z.string().uuid(),
  stripePriceId: z.string().startsWith('price_'), // Basic validation for Stripe Price ID format
});

export const createAndAssignCustomPlanInputSchema = z.object({
  organizationId: z.string().uuid(),
  planName: z.string().min(1, 'Plan name is required'),
  planDescription: z.string().optional(),
  maxUsers: z.number().int().min(0, 'Max users cannot be negative'),
  maxDocuments: z.number().int().min(0, 'Max documents cannot be negative'),
  monthlyBudget: z.number().min(0, 'Monthly budget cannot be negative'),
  priceAmount: z.number().positive('Price must be positive'),
  currency: z.nativeEnum(CurrencyEnum),
  billingInterval: billingIntervalEnum.default(BillingInterval.Month),
  autoRenewal: z.boolean().optional().default(true),
  autoRenewalAmount: z
    .number()
    .positive()
    .min(5, { message: 'Auto-renewal amount must be at least $5' })
    .nullable()
    .optional(),
  autoRenewalLimit: z.number().nonnegative().nullable().optional(),
  autoRenewalThreshold: z
    .number()
    .positive({ message: 'Threshold must be positive' })
    .min(5, { message: 'Threshold must be at least $5.00' })
    .nullable()
    .optional(),
});

export type SubscriptionCheckoutFrontendInput = Omit<
  z.infer<typeof createCheckoutInputSchema>,
  'organizationId'
>;

export type SubscriptionUpdateFrontendInput = Omit<
  z.infer<typeof updateSubscriptionInputSchema>,
  'organizationId'
>;

// Schema for the dropdown list item - Includes details from Product/Price
export const customPlanListItemSchema = z.object({
  priceId: z.string(),
  productName: z.string(),
  amount: z.number(),
  interval: billingIntervalEnum,
  currency: z.string(),
  maxUsers: z.number().int().min(0).optional(),
  maxDocuments: z.number().int().min(0).optional(),
  monthlyBudget: z.number().min(0).optional(),
  metadata: z.record(z.string(), z.string().nullable()).optional(),
});

export type CustomPlanListItemDTO = z.infer<typeof customPlanListItemSchema>;

// --- Add Schema for Currency/Plan Change ---
export const changeCurrencyAndPlanInputSchema = z.object({
  organizationId: z.string().uuid('Invalid Organization ID'),
  targetCurrency: z.nativeEnum(CurrencyEnum),
  targetTier: z.nativeEnum(SubscriptionTierEnum),
  targetBillingInterval: z.nativeEnum(BillingIntervalEnum),
  // Price ID is optional, primarily for assigning specific custom Enterprise plans during switch
  targetPriceId: z.string().startsWith('price_').optional(),
});

export type ChangeCurrencyAndPlanInputDTO = z.infer<
  typeof changeCurrencyAndPlanInputSchema
>;
// --- Add Manual Balance Schema ---
export const addManualBalanceInputSchema = z.object({
  organizationId: z.string().min(1, 'Organization ID is required'),
  amountToAdd: z.number().positive('Amount must be positive').finite().safe(),
  currency: z.nativeEnum(CurrencyEnum),
  reason: z.string().optional(), // Optional: Add a reason for auditing
});

/**
 *  Schema for updating subscription via Stripe
 */
export const updateSubscriptionLimitsSchema = z.object({
  organizationId: z.string().uuid('Invalid organization ID'),
  maxUsers: z.number().min(1).step(1).optional(),
  maxDocuments: z.number().min(0).step(1).optional(),
});
